/**
 * Request Signing Utility (Frontend)
 * Signs checkout requests with HMAC-SHA256
 * Allows backend to verify requests come from legitimate SaaS
 */

/**
 * Sign a request payload with HMAC-SHA256
 * Uses the SaaS secret key to create a cryptographic signature
 *
 * @param payload - The request body as JSON string
 * @param secret - The SaaS secret key (configured in .env)
 * @returns HMAC-SHA256 signature in hex format
 */
async function signRequest(payload: string, secret: string): Promise<string> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secret);
  const messageData = encoder.encode(payload);

  // Import the key using Web Crypto API
  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  // Sign the message
  const signature = await crypto.subtle.sign('HMAC', key, messageData);

  // Convert to hex string
  const signatureArray = Array.from(new Uint8Array(signature));
  return signatureArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a random nonce to prevent replay attacks
 * @returns Random hex string
 */
function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Create signing headers for a checkout request
 * Adds X-Signature and X-Nonce headers for verification on backend
 *
 * @param payload - The request body as JSON string
 * @param secret - The SaaS secret key from environment
 * @returns Object with headers { 'X-Signature': '...', 'X-Nonce': '...' }
 *
 * @example
 * const headers = await createSigningHeaders(
 *   JSON.stringify({ organization_id: 'org123', ... }),
 *   'your-saas-secret-key'
 * );
 *
 * fetch('/api/checkout/proxy/sessions', {
 *   method: 'POST',
 *   headers: {
 *     'Content-Type': 'application/json',
 *     ...headers
 *   },
 *   body: payload
 * });
 */
export async function createSigningHeaders(
  payload: string,
  secret: string
): Promise<Record<string, string>> {
  const nonce = generateNonce();
  // Include nonce in signature to prevent replay attacks
  const signature = await signRequest(payload + nonce, secret);

  return {
    'X-Signature': signature,
    'X-Nonce': nonce,
  };
}

/**
 * Sign and send a checkout request
 * Automatically adds signing headers before sending
 *
 * @param url - The API endpoint URL
 * @param payload - The request data object
 * @param secret - The SaaS secret key
 * @param options - Additional fetch options
 * @returns The fetch response
 */
export async function signedFetch(
  url: string,
  payload: unknown,
  secret: string,
  options: Omit<RequestInit, 'body'> = {}
): Promise<Response> {
  const payloadString = JSON.stringify(payload);
  const signingHeaders = await createSigningHeaders(payloadString, secret);

  return fetch(url, {
    ...options,
    body: payloadString,
    headers: {
      'Content-Type': 'application/json',
      ...signingHeaders,
      ...options.headers,
    },
  });
}

/**
 * Generate session proof for public endpoints
 * Proves that the caller has access to the full session data
 * Uses HMAC-SHA256 with session data
 *
 * @param sessionId - The public session ID (used in URLs)
 * @param organizationId - The organization ID from session
 * @param expiresAt - The session expiration timestamp
 * @returns HMAC-SHA256 signature in hex format
 */
export async function generateSessionProof(
  sessionId: string,
  organizationId: string,
  expiresAt: string
): Promise<string> {
  // Use session_id:organization_id:expires_at as the data to sign
  // Use session_id as the HMAC secret key
  const sessionData = `${sessionId}:${organizationId}:${expiresAt}`;
  return signRequest(sessionData, sessionId);
}
